import { useEffect, useRef } from 'react';
import { reaction } from 'mobx';
import { roomInfoStore } from 'state/store';
import { STAGE_LAYOUT, type StageLayout } from 'consts';
import { isWebinarFeatureOn } from 'useCase/featureFlags/featureFlagsApi';

const trackWebinarEnabled = () => roomInfoStore.metadata?.webinarMode?.enabled;

/**
 * Модератор, локально включивший вебинар, один раз пропускает авто SPEAKER.
 * Выставляется перед optimistic setWebinarMode({ enabled: true }).
 */
let skipSpeakerLayoutForLocalEnabler = false;

/** Пользователь вручную выбрал Grid во время вебинара — до выхода из звонка авто-layout не трогаем. */
let userOptedOutOfWebinarSpeakerLayout = false;

export const skipWebinarSpeakerLayoutForLocalEnabler = (): void => {
    skipSpeakerLayoutForLocalEnabler = true;
};

export const markWebinarSpeakerLayoutManualOptOut = (): void => {
    userOptedOutOfWebinarSpeakerLayout = true;
};

/** Сбрасывается при входе в комнату — повторный join с активным вебинаром снова даёт SPEAKER. */
export const resetWebinarSpeakerLayoutOptOut = (): void => {
    userOptedOutOfWebinarSpeakerLayout = false;
};

const shouldApplyWebinarSpeakerLayout = (): boolean => {
    if (userOptedOutOfWebinarSpeakerLayout) {
        return false;
    }

    if (skipSpeakerLayoutForLocalEnabler) {
        skipSpeakerLayoutForLocalEnabler = false;
        return false;
    }

    return true;
};

export const useWebinarAutoLayout = (setStageLayout: (layout: StageLayout) => void) => {
    const setStageLayoutRef = useRef(setStageLayout);
    setStageLayoutRef.current = setStageLayout;

    /**
     * SPEAKER по умолчанию для вебинара:
     * - модератор включил вебинар (кроме того, кто включил);
     * - новый участник / rejoin при уже активном вебинаре.
     *
     * Не трогаем layout при:
     * - выключении вебинара;
     * - смене роли (модератор/спикер);
     * - ручном выборе Grid (до выхода из звонка).
     */
    useEffect(() => {
        const applySpeakerLayoutWhenWebinarBecomesActive = (
            enabled: boolean | undefined,
            prevEnabled: boolean | undefined,
        ) => {
            if (!isWebinarFeatureOn()) return;
            if (!enabled) return;
            if (prevEnabled === true) return;

            if (!shouldApplyWebinarSpeakerLayout()) return;

            setStageLayoutRef.current(STAGE_LAYOUT.SPEAKER);
        };

        return reaction(trackWebinarEnabled, applySpeakerLayoutWhenWebinarBecomesActive, { fireImmediately: true });
    }, []);
};
